import React from 'react';
import { DeckCard } from '../../types/pokemon';
import { SUPABASE_IMAGE_BASE_URL } from '../../lib/constants';
import { useTranslation } from '../../hooks/useTranslation';

interface DeckPreviewCardsProps {
  cards: DeckCard[];
  maxCards?: number;
  mobileMode?: boolean;
  totalUniqueCards?: number;
}

export function DeckPreviewCards({ 
  cards, 
  maxCards = 10, 
  mobileMode = false,
  totalUniqueCards
}: DeckPreviewCardsProps) {
  const { t, lang } = useTranslation();
  
  // Sort cards by rarity before displaying
  const sortedCards = [...cards].sort((a, b) => {
    const rarityOrder = {
      '♛': 1,    // Couronne
      '☆☆☆': 2,  // 3 étoiles
      '☆☆': 3,   // 2 étoiles
      '☆': 4,    // 1 étoile
      '♢♢♢♢': 5, // 4 diamants
      '♢♢♢': 6,  // 3 diamants
      '♢♢': 7,   // 2 diamants
      '♢': 8     // 1 diamant
    };
    
    const rarityA = rarityOrder[a.card?.rarity as keyof typeof rarityOrder] || 999;
    const rarityB = rarityOrder[b.card?.rarity as keyof typeof rarityOrder] || 999;
    return rarityA - rarityB;
  });

  const displayCards = sortedCards.slice(0, maxCards);
  
  // Calculer le nombre de cartes restantes
  // Si totalUniqueCards est fourni, l'utiliser pour les cartes uniques restantes
  // Sinon, utiliser le nombre total d'exemplaires
  const remainingCards = totalUniqueCards 
    ? totalUniqueCards - maxCards 
    : cards.length - maxCards;

  // Seul le countSize est encore utilisé pour l'affichage de la quantité
  const countSize = mobileMode ? "w-4 h-4 text-[10px]" : "w-5 h-5 text-xs";

  return (
    <div className={`flex flex-wrap ${mobileMode ? 'gap-1' : 'gap-1.5'} justify-center`}>
      {displayCards.map((deckCard) => {
        const card = deckCard.card;
        if (!card?.image) return null;

        // Modifier le chemin d'image en fonction de la langue
        // Pour l'anglais, placer "en/" avant "preview/"
        const imagePath = lang === 'en' ? `en/preview/${card.image}` : `preview/${card.image}`;
        const cardName = lang === 'en' ? card.card_en || card.card_fr : card.card_fr || card.card_en;

        return (
          <div
            key={`${card.id}-${deckCard.quantity}`}
            className={`relative ${mobileMode ? 'w-[calc(25%-3px)]' : 'w-[calc(20%-6px)]'} transition-transform hover:scale-105`}
          >
            <img
              src={`${SUPABASE_IMAGE_BASE_URL}${imagePath}`}
              alt={cardName || 'Pokémon Card'}
              className="w-full h-auto rounded shadow-sm"
              loading="lazy"
            />
            {deckCard.quantity > 1 && (
              <span className={`absolute -top-1 -right-1 bg-indigo-600 text-white font-bold rounded-full flex items-center justify-center shadow-md ${countSize}`}>
                {deckCard.quantity}
              </span>
            )}
          </div>
        );
      })}
      
      {remainingCards > 0 && (
        <div 
          className={`flex items-center justify-center ${mobileMode ? 'w-[calc(25%-3px)]' : 'w-[calc(20%-6px)]'} aspect-[2.5/3.5] bg-indigo-50 border border-indigo-100 rounded shadow-sm text-center`}
        >
          <div className="text-indigo-600 font-medium">
            <span className="block text-lg">+{remainingCards}</span>
            <span className="text-xs">
              {remainingCards === 1 
                ? t('public_decks.deck_details.plus_cards').replace('{{count}}', remainingCards.toString()) 
                : t('public_decks.deck_details.plus_cards_plural').replace('{{count}}', remainingCards.toString())}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}